import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// Links visíveis no iPad (resumidos)
const ipadLinks = [
  { path: "/biblioteca", label: "Biblioteca" },
  { path: "/cursos", label: "Cursos" },
  { path: "/sobre", label: "Sobre" },
  { path: "/contato", label: "Contato" },
];

// Todos os links (desktop e menu hamburguer)
const allLinks = [
  { path: "/", label: "Início" },
  { path: "/app", label: "App" },
  { path: "/biblioteca", label: "Biblioteca" },
  { path: "/cursos", label: "Cursos" },
  { path: "/comunidade", label: "Comunidade" },
  { path: "/sobre", label: "Sobre" },
  { path: "/contato", label: "Contato" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAdmin, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMenuAberto(false);
  }, [location.pathname]);

  async function handleLogout() {
    await signOut();
    setMenuAberto(false);
    navigate("/");
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-godoy-black/95 backdrop-blur-md border-b border-godoy-gold/20 shadow-lg shadow-godoy-gold/5"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo — funciona como botão Início */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0">
              <img
                src="https://pub-bc092def332d4fb180f02b510802da8e.r2.dev/LogoGP.png"
                alt="GodoyProd"
                className="w-full h-full object-contain"
              />
            </div>
            <span
              className="font-serif text-xl md:text-2xl font-semibold text-godoy-cream tracking-[0.15em] uppercase group-hover:text-godoy-gold transition-colors"
              style={{ textShadow: "0 2px 40px rgba(201,168,76,0.3)" }}
            >
              GodoyProd
            </span>
          </Link>

          {/* ===== DESKTOP (lg+): todos os links ===== */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {allLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-godoy-gold whitespace-nowrap ${
                  location.pathname === link.path ? "text-godoy-gold" : "text-godoy-cream/80"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuAberto(!menuAberto)}
                  className="flex items-center gap-2 px-4 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm font-medium text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                >
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <i className="ri-user-line" />
                  )}
                  {profile?.nome || "Minha Conta"}
                  <i className={`ri-arrow-down-s-line transition-transform ${menuAberto ? "rotate-180" : ""}`} />
                </button>
                {menuAberto && <UserDropdown profile={profile} user={user} isAdmin={isAdmin} onClose={() => setMenuAberto(false)} onLogout={handleLogout} />}
              </div>
            ) : (
              <Link to="/login" className="px-5 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm font-medium text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap">
                Entrar
              </Link>
            )}
          </div>

          {/* ===== IPAD (md..lg): links resumidos + hamburguer ===== */}
          <div className="hidden md:flex lg:hidden items-center gap-4">
            {ipadLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-godoy-gold whitespace-nowrap ${
                  location.pathname === link.path ? "text-godoy-gold" : "text-godoy-cream/80"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Botão conta no iPad */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuAberto(!menuAberto)}
                  className="flex items-center gap-2 px-3 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm font-medium text-godoy-gold hover:bg-godoy-gold/20 transition-all"
                >
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <i className="ri-user-line" />
                  )}
                  <i className={`ri-arrow-down-s-line transition-transform ${menuAberto ? "rotate-180" : ""}`} />
                </button>
                {menuAberto && <UserDropdown profile={profile} user={user} isAdmin={isAdmin} onClose={() => setMenuAberto(false)} onLogout={handleLogout} />}
              </div>
            ) : (
              <Link to="/login" className="px-3 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm font-medium text-godoy-gold hover:bg-godoy-gold/20 transition-all">
                Entrar
              </Link>
            )}

            {/* Hamburguer no iPad para links extras (App, Comunidade, etc.) */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-godoy-cream hover:text-godoy-gold transition-colors"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
            </button>
          </div>

          {/* ===== MOBILE (< md): só hamburguer ===== */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-godoy-cream"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Menu hamburguer expandido (mobile + iPad) */}
      {mobileOpen && (
        <div className="lg:hidden bg-godoy-black/98 backdrop-blur-lg border-t border-godoy-gold/20">
          <div className="px-6 py-4 space-y-4">
            {allLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-base font-medium transition-colors hover:text-godoy-gold ${
                  location.pathname === link.path ? "text-godoy-gold" : "text-godoy-cream/80"
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <div className="border-t border-godoy-gray/20 pt-3 space-y-3">
                  <Link to="/meu-perfil" className="block text-base font-medium text-godoy-gold">Meu Perfil</Link>
                  <Link to="/minha-conta" className="block text-base font-medium text-godoy-gold">Minhas Partituras</Link>
                  <Link to="/minha-playlist" className="block text-base font-medium text-godoy-gold">Minha Playlist</Link>
                  {isAdmin && (
                    <Link to="/admin/partituras" className="block text-base font-medium text-godoy-gold">Painel Admin</Link>
                  )}
                  <button onClick={handleLogout} className="block text-base font-medium text-red-400">Sair</button>
                </div>
              </>
            ) : (
              <Link to="/login" className="block w-full text-center px-5 py-2.5 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm font-medium text-godoy-gold hover:bg-godoy-gold/20 transition-all">
                Entrar
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// Componente dropdown reutilizável
function UserDropdown({ profile, user, isAdmin, onClose, onLogout }: any) {
  return (
    <div className="absolute right-0 mt-2 w-60 bg-godoy-charcoal border border-godoy-gray/30 rounded-lg shadow-xl z-50 overflow-hidden">
      <div className="px-4 py-3 border-b border-godoy-gray/20 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full overflow-hidden border border-godoy-gold/30 flex-shrink-0">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-godoy-charcoal flex items-center justify-center">
              <i className="ri-user-line text-godoy-lightgray" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-godoy-cream text-sm font-medium truncate">{profile?.nome || "Usuário"}</p>
          <p className="text-godoy-lightgray/60 text-xs truncate">{user?.email}</p>
        </div>
      </div>
      <Link to="/meu-perfil" onClick={onClose} className="flex items-center gap-3 px-4 py-3 text-sm text-godoy-cream hover:bg-godoy-gold/10 hover:text-godoy-gold transition-colors">
        <i className="ri-edit-line" /> Editar Perfil
      </Link>
      <Link to="/minha-conta" onClick={onClose} className="flex items-center gap-3 px-4 py-3 text-sm text-godoy-cream hover:bg-godoy-gold/10 hover:text-godoy-gold transition-colors">
        <i className="ri-book-open-line" /> Minhas Partituras
      </Link>
      <Link to="/minha-playlist" onClick={onClose} className="flex items-center gap-3 px-4 py-3 text-sm text-godoy-cream hover:bg-godoy-gold/10 hover:text-godoy-gold transition-colors">
        <i className="ri-play-list-line" /> Minha Playlist
      </Link>
      {isAdmin && (
        <Link to="/admin/partituras" onClick={onClose} className="flex items-center gap-3 px-4 py-3 text-sm text-godoy-cream hover:bg-godoy-gold/10 hover:text-godoy-gold transition-colors">
          <i className="ri-shield-user-line" /> Painel Admin
        </Link>
      )}
      <div className="border-t border-godoy-gray/20" />
      <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-godoy-cream hover:bg-red-500/10 hover:text-red-400 transition-colors text-left">
        <i className="ri-logout-box-r-line" /> Sair
      </button>
    </div>
  );
}